import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { joinDisruption } from '@/lib/ws/socket';
import { useConnectionStore } from '@/state/connection.store';
import { fetchWhy } from './api';
import { WHY_KEY_ROOT, whyKey } from './keys';
import type { WhyScope, WhyView } from './types';

// The account, live (feature disruption-why, slice W6).
//
// ONE HTTP read when the dialog opens, then the WS owns freshness:
// `NarrativeChanged@v1` → `applyEvent` → `setQueryData(['why', …])`. There is no
// timer, no interval and no react-query polling option anywhere in this
// feature — a polled "live" surface is forbidden by brief §6.10 /
// frontend-conventions §15.
// The only extra read that ever fires is the single reconnect catch-up below,
// which is an EVENT (socket 'connect'), not a clock.

/** Key used while the dialog is closed — never fetched (`enabled:false`). */
const IDLE_KEY: readonly unknown[] = [WHY_KEY_ROOT, 'idle'];

/**
 * The `why@v1` view for a disruption or a case.
 *
 * `staleTime: Infinity` + `refetchOnWindowFocus:false` because the socket is the
 * refresh mechanism; letting TanStack re-fetch on its own would race the WS
 * merge and, in a hub closure with a dialog open per operator, hammer the BFF.
 *
 * ROOM MEMBERSHIP. `NarrativeChanged` is fanned into `disruption:<urn>`, which
 * Tower and Express already join — this joins it again (idempotent) so the
 * dialog is also live when opened from a CASE, where nothing else joined it.
 * Deliberately NOT paired with a `leaveDisruption` on unmount: closing this
 * dialog must not silently kill the Tower's own live feed for the same
 * disruption (the `useCaseTrips` precedent). On the case route the extra
 * membership is inert — no cached view reads it once the dialog is gone.
 *
 * On the case route the disruption is not known until the answer arrives, so
 * the join follows `query.data.disruptionUrn` when the caller had no hint.
 */
export function useWhy(scope: WhyScope | null, enabled = true) {
  const active = enabled && !!scope;

  const query = useQuery<WhyView>({
    queryKey: scope ? whyKey(scope) : IDLE_KEY,
    queryFn: () => fetchWhy(scope as WhyScope),
    enabled: active,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const roomUrn =
    scope?.kind === 'disruption'
      ? scope.disruptionUrn
      : (scope?.disruptionUrn ?? query.data?.disruptionUrn ?? undefined);

  useEffect(() => {
    if (!active || !roomUrn) return;
    joinDisruption(roomUrn);
  }, [active, roomUrn]);

  // WS-DROP HANDLING. A dropped socket loses the pushes AND the room membership
  // that would deliver the next ones. socket.io reconnects on its own and the
  // connection store flips `connected` back to true — hang the recovery off THAT
  // transition: re-join (rooms do not survive a reconnect; the join is
  // idempotent) plus exactly ONE refetch for whatever landed while we were deaf.
  // One event, one read.
  const connected = useConnectionStore((s) => s.connected);
  const wasConnected = useRef(connected);
  const { refetch } = query;
  useEffect(() => {
    const reconnected = connected && !wasConnected.current;
    wasConnected.current = connected;
    if (!reconnected || !active || !roomUrn) return;
    joinDisruption(roomUrn);
    void refetch();
  }, [connected, active, roomUrn, refetch]);

  return query;
}

/** `true` when the account half holds nothing at all — the dialog's EMPTY state. */
export function isAccountEmpty(view: WhyView | undefined): boolean {
  if (!view) return true;
  return !view.summary && view.chapters.length === 0;
}
